import gtm from '@/utils/gtm';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import EfInfoSection from '../ef-info-section';
import EFPicture from '../ef-picture';
import EfVideo from '../ef-video';
import './index.scss';

interface EfSwiperTabItemProps {
    /**
     * 资源链接是否是视频
     */
    isVideo?: boolean;
    /**
     * 所有分辨率展示的资源链接
     */
    pc: string;
    /**
     * 分辨率小于等于1024px | 768px 展示的资源链接
     */
    h5?: string;
    /**
     * 分辨率小于等于1024px展示的资源链接
     */
    pad?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * tab切换的主标题
     */
    title?: string | any;
    /**
     * tag
     */
    tag?: string;
    /**
     * customizedEl
     */
    customizedEl?: any;
    /**
     * tab标签
     */
    tab: string;
    /**
     * tab描述
     */
    desc?: string;
    /**
     * link文本
     */
    linkText?: string;
    /**
     * link链接url
     */
    linkUrl?: string;
}

export interface EfSwiperTabProps {
    /**
     * swiper是否循环
     */
    loop?: boolean;
    /**
     * 主标题、描述是否随着切换
     */
    hasInfo?: boolean;
    /**
     * 段落标题
     */
    title?: string;
    /**
     * 间隔
     */
    gap?: number;
    /**
     * 轮播按钮navigation
     */
    navigation?: boolean;
    /**
     * 轮播按钮pagination
     */
    pagination?: boolean;
    /**
     * 初始轮播index
     */
    initialSlide?: number;
    /**
     * 主题色
     */
    theme?: 'white' | 'black';
    /**
     * 样式名
     */
    className?: string;
    /**
     * 列表
     */
    lists: EfSwiperTabItemProps[];
    /**
     * callback
     */
    callback?: any;
}

/**
 * 维护: kalkin
 *
 * tab联动swiper切换
 */
const EfSwiperTab: React.FC<EfSwiperTabProps> = ({
    navigation = true,
    pagination = true,
    initialSlide = 0,
    gap,
    theme = 'white',
    className,
    lists,
    title = '',
    hasInfo,
    callback,
    loop = true,
}) => {
    const prefixCls = `ef-swiper-tab`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-${theme}`]: theme,
    });
    const [space, setSpace] = useState(gap ?? 36);
    const [active, setActive] = useState(initialSlide);
    const [swiper, setSwiper] = useState(null);

    const gtmEvent = (idx: number) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'tab_click',
            list_name: hasInfo ? lists[idx]?.title : title || 'none',
            button_name: lists[idx]?.tab || lists[idx]?.title,
        });
    };

    const handleClickTab = (index: number) => {
        setActive(index);
        if (swiper) {
            if (loop) {
                swiper.slideToLoop(index);
            } else {
                swiper.slideTo(index);
            }
        }
        callback && callback(index);
        //gtmEvent(index);
    };

    const handleSlideChange = (swiper: any) => {
        setActive(swiper?.realIndex);
        handleClickTab(swiper?.realIndex);
        callback && callback(swiper?.realIndex);
        // gtmEvent(swiper?.realIndex);
    };

    useEffect(() => {
        if (typeof window !== 'undefined' && !gap && gap !== 0) {
            const g = window?.innerWidth < 768 ? 0 : window?.innerWidth <= 1024 ? 24 : 36;
            setSpace(g);
        }
        if (typeof window !== 'undefined' && (gap || gap === 0)) {
            setSpace(gap);
        }
    }, [gap]);

    return (
        <div className={classes}>
            {hasInfo && (
                // <ul className={`${prefixCls}-info-lists`}>
                //     {lists?.map((list: EfSwiperTabItemProps, k: number) => (
                //         <li className={`${prefixCls}-info-list`} key={k}>
                <EfInfoSection
                    className={`${prefixCls}-info-list`}
                    icon={lists[active]?.icon || ''}
                    label={lists[active]?.label || ''}
                    title={lists[active]?.title}
                    subtitle={lists[active]?.subtitle || ''}
                    desc={lists[active]?.desc || ''}
                    linkText={lists[active]?.linkText || ''}
                    linkUrl={lists[active]?.linkUrl || ''}
                />
                //         </li>
                //     ))}
                // </ul>
            )}
            <Swiper
                className={`${prefixCls}-swiper`}
                modules={[Navigation, Pagination]}
                navigation={navigation}
                pagination={pagination}
                loop={lists?.length > 1 ? loop : false}
                spaceBetween={space}
                centeredSlides={true}
                slidesPerView={'auto'}
                initialSlide={initialSlide}
                onSlideChange={handleSlideChange}
                onSwiper={(swiperIns: any) => setSwiper(swiperIns)}
            >
                {lists?.map((item: any, k: number) => (
                    <SwiperSlide className={`${prefixCls}-swiper-item${active === k ? ` ${prefixCls}-swiper-item-active` : ''}`} key={k}>
                        <div className={`${prefixCls}-swiper-item-img`}>
                            <EFPicture
                                src={item?.poster || item.pc}
                                srcDesktop={item?.poster || item.desktop}
                                srcPad={item?.poster || item?.pad || item?.h5 || item.pc}
                                srcMobile={item?.poster || item?.h5 || item.pc}
                                alt={item?.tab}
                            />
                        </div>
                        {active === k && item?.isVideo && (
                            <div className={`${prefixCls}-swiper-item-video`}>
                                <EfVideo pc={item.pc} h5={item?.h5 || item?.pc} poster={item?.poster} autoPlay={true} loop={true} />
                            </div>
                        )}
                        {item?.customizedEl && item?.customizedEl}
                        {active !== k && <div className={`${prefixCls}-swiper-item-mask`}></div>}
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className={`${prefixCls}-info`}>
                <ul className={`${prefixCls}-tabs`}>
                    {lists?.map((item: EfSwiperTabItemProps, k: number) => (
                        <li
                            className={`${prefixCls}-tab${active === k ? ` ${prefixCls}-tab-active` : ''}`}
                            key={k}
                            onClick={() => {
                                gtmEvent(k);
                                handleClickTab(k);
                            }}
                        >
                            {item?.tab}
                        </li>
                    ))}
                </ul>
                {lists[active]?.desc && !hasInfo && <p className={`${prefixCls}-desc`}>{lists[active]?.desc}</p>}
            </div>
        </div>
    );
};

export default EfSwiperTab;
